import { useState } from 'react'
import { X, Edit2, Check } from 'lucide-react'
import { Badge } from '@lovable/components/ui/badge'
import { Button } from '@lovable/components/ui/button'
import { Textarea } from '@lovable/components/ui/textarea'
import { t } from 'i18next'

interface GoalCardProps {
  id: number
  index: number
  title: string
  description: string
  onRemove: () => void
  onRefine: (title: string, description: string) => void
}

export function GoalCard({ id, index, title, description, onRemove, onRefine }: GoalCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(title)
  const [editDescription, setEditDescription] = useState(description)

  const handleSave = () => {
    onRefine(editTitle, editDescription)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditTitle(title)
    setEditDescription(description)
    setIsEditing(false)
  }

  return (
    <div className='p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/30 transition-all group'>
      <div className='flex items-start gap-3'>
        <Badge variant='outline' className='mt-0.5 shrink-0'>
          {index + 1}
        </Badge>

        <div className='flex-1 min-w-0'>
          {isEditing ? (
            <div className='space-y-2'>
              <input
                type='text'
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className='w-full px-2 py-1 text-sm font-semibold bg-background border border-border rounded'
                placeholder='Goal title'
              />
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className='min-h-[60px] text-sm'
                placeholder='Goal description'
              />
              <div className='flex gap-2'>
                <Button variant='outline' className='shadow-md hover:shadow-lg hover:bg-primary transition-all' size='sm' onClick={handleSave}>
                  <Check className='w-3 h-3 mr-1' />
                  {t('engine.edit_goal.save', 'Save')}
                </Button>
                <Button size='sm' variant='ghost' onClick={handleCancel}>
                  {t('engine.edit_goal.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h3 className='font-semibold text-foreground mb-1'>{title}</h3>
              <p className='text-sm text-muted-foreground'>{description}</p>
            </>
          )}
        </div>

        <div className='flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0'>
          {!isEditing && (
            <>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setIsEditing(true)}
                className='h-8 w-8 p-0 hover:text-primary'
              >
                <Edit2 className='w-3 h-3' />
              </Button>
              <Button
                variant='ghost'
                size='sm'
                onClick={onRemove}
                className='h-8 w-8 p-0 text-destructive hover:text-destructive'
              >
                <X className='w-3 h-3' />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
